import { createReducer } from "reduxsauce"
import { findIndex, forEach, get, pick, without } from "lodash"
import createActions from "helpers/actionCreator"
import { Types as CompositeTypes } from "modules/analyze-composite-security/model"
import { createDefaultReducer } from "helpers/reducers"

export const { Types, Creators } = createActions("ompnt/modules/analyzeCompositeExposure", {
  focusChartLine: {
    props: ["focusedChartLine"],
    track: false,
  },
  blurChartLine: { track: false },
  setAggregationFunction: {
    props: ["functionName"],
    trackType: "composite-style-exposure-aggregation-function-selection",
  },
  addSelectedView: {
    props: ["factorId"],
    trackType: "composite-analyze-trends-select-view-factor-add",
  },
  removeSelectedView: {
    props: ["factorId", "options"],
    trackType: "composite-analyze-trends-select-view-factor-removal",
    trackPayload: props => pick(props, "factorId"),
  },
  selectFactorCategory: {
    props: ["factorId"],
    trackType: "composite-analyze-exposure-category-selection",
  },
  toggle: {
    props: ["factorId", "currentState"],
    trackType: "composite-analyze-exposure-graph-legend-factor",
    trackPayload: ({ factorId, currentState }) => ({ factor: factorId, state: currentState ? "on" : "off" }),
  },
  toggleOnly: {
    props: ["factorId", "only", "items", "contentSetId"],
    trackType: "composite-analyze-exposure-graph-legend-factor",
    trackPayload: ({ factorId, contentSetId }) => ({ factor: factorId, contentSetId }),
  },
  setMetricType: ["metricType"],
}, { track: true })

const INITIAL_STATE = {
  focusedChartLine: null,
}

const handleToggle = (state, { factorId, currentState }) => ({ ...state,
  focusedChartLine: currentState ? factorId : null,
})

const handleToggleOnly = (state, { factorId, only }) => ({
  ...state,
  focusedChartLine: only ? factorId : null,
})

export const HANDLERS = {
  [Types.FOCUS_CHART_LINE]: createDefaultReducer(),
  [Types.BLUR_CHART_LINE]: createDefaultReducer({ customStateValues: { focusedChartLine: null } }),
  [Types.TOGGLE]: handleToggle,
  [Types.TOGGLE_ONLY]: handleToggleOnly,
}

export default createReducer(INITIAL_STATE, HANDLERS)

const resetGraphState = (state) => ({
  ...state,
  disabled: null,
  only: null,
  focusedChartLine: null,
})

const handleSetAggregationFunction = (state, { functionName }) => ({
  ...state,
  styleAggregation: functionName !== "all" ? functionName : null,
})

const handleAddSelectedView = (state, { factorId }) => ({
  ...state,
  views: [...(state.views || []), factorId],
  factorCategory: factorId,
})

const handleRemoveSelectedView = (state, { factorId, options }) => {
  let { factorCategory } = state
  if (factorCategory === factorId) {
    const optionIndex = findIndex(options, option => option.id === factorId)
    factorCategory = get(options, `${optionIndex - 1}.id`)
  }

  return {
    ...state,
    factorCategory,
    views: without(state.views, factorId),
  }
}

const handleSelectFactorCategory = (state, { factorId }) => ({
  ...resetGraphState(state),
  factorCategory: factorId,
})

const handleToggleQS = (state, { factorId, currentState }) => ({
  ...state,
  only: null,
  disabled: currentState ? without(state.disabled, factorId) : [...(state.disabled || []), factorId],
})

const handleToggleOnlyQS = (state, { factorId, only, items }) => {
  const disabled = []
  let onlyDisplay = null

  if (only) {
    onlyDisplay = factorId
    forEach(items, item => {
      if (item.id !== factorId) {
        disabled.push(item.id)
      }
    })
  }

  return { ...state, disabled, only: onlyDisplay }
}

const handleSetMetricType = (state, { metricType }) => ({
  ...state,
  metricType,
})

export const queryStringReducers = {
  [Types.SET_AGGREGATION_FUNCTION]: handleSetAggregationFunction,
  [Types.ADD_SELECTED_VIEW]: handleAddSelectedView,
  [Types.REMOVE_SELECTED_VIEW]: handleRemoveSelectedView,
  [Types.SELECT_FACTOR_CATEGORY]: handleSelectFactorCategory,
  [Types.TOGGLE]: handleToggleQS,
  [Types.TOGGLE_ONLY]: handleToggleOnlyQS,
  [Types.SET_METRIC_TYPE]: handleSetMetricType,
  [CompositeTypes.SET_MODEL]: resetGraphState,
}
